import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { poundsToKgs, kgsToPounds } from '../../helpers/utils';
import {
  saveBodyFat,
  clearBodyFatErrors,
} from '../../ducks/Measurements/actions';
import { CLOSE_DRAWER } from '../../ducks/Global/constants';
import ProgressUpload from '../../components/ProgressUpload/index';
import { getNutrition } from '../../ducks/Profile/actions';
import './index.css';
import moment from 'moment';
import { getTodo } from '../../ducks/Todo/actions';
import { toast } from '../../components/Toast';
import { hasMinimumRole } from '../../utils/permissions';
import WeightField from '../../components/WeightField';

class ManualForm extends React.Component {
  constructor(props) {
    super(props);
    // Default the WeightField unit. Phase 2 honors the trainer's
    // own `preferredUnits` setting when one's set — a US-based
    // trainer working with metric clients lands on `lb` instead of
    // following each client's profile preference. Falls back to
    // the client's `data.isMetric` when the viewer has no trainer
    // preference (e.g. clients editing their own measurements).
    const trainerPref = props.viewerPreferredUnits;
    const profilePref = props.data?.isMetric === false ? 'lb' : 'kg';
    const initialWeightUnit = trainerPref
      ? trainerPref === 'imperial'
        ? 'lb'
        : 'kg'
      : profilePref;
    this.state = {
      bodyFatObj: {},
      progressPictures: [],
      weightInputUnit: initialWeightUnit,
    };
  }

  componentDidUpdate(prevProps) {
    // Re-seed the unit toggle when the trainer pref hydrates late.
    // `state.auth.user.trainerPreferences` arrives via the /api/auth
    // saga, which can resolve AFTER ManualForm mounts (the modal
    // opens immediately on click); without this, the form would
    // start on the client's `isMetric` and never catch up to the
    // trainer's pref. Manual pill clicks don't bounce here because
    // they don't change `viewerPreferredUnits` on props.
    if (
      prevProps.viewerPreferredUnits !== this.props.viewerPreferredUnits &&
      this.props.viewerPreferredUnits
    ) {
      const pref = this.props.viewerPreferredUnits;
      this.setState({ weightInputUnit: pref === 'imperial' ? 'lb' : 'kg' });
    }
    if (
      this.props.userBodyFatMeasurements &&
      prevProps.userBodyFatMeasurements &&
      this.props.userBodyFatMeasurements.length !==
        prevProps.userBodyFatMeasurements.length
    ) {
      this.props.getNutrition();
      // Close the modal after successful save
      if (this.props.closeModal) {
        this.props.closeModal();
      }
      this.setState({ bodyFatObj: {} });
      toast.success('Measurement saved successfully!');

      // Land the user on the surface that displays the new entry, so
      // they immediately see their data reflected (closes the
      // feedback loop):
      //   - Trainer logging for a client → that client's profile
      //     measurements tab (in-place tab switch when they were
      //     already on that profile, which is the common case).
      //   - Client logging for themselves (or trainer using as a
      //     client) → their own progress page.
      // Skip the navigation if they're already on the destination
      // route so we don't push duplicate history entries.
      const targetUserId = this.props.data?.user?._id;
      const authedUserId = this.props.user?._id;
      const isTrainerLogForOther =
        targetUserId &&
        authedUserId &&
        String(targetUserId) !== String(authedUserId);
      const dest = isTrainerLogForOther
        ? `/dashboard/admin/clients/${targetUserId}?tab=measurements`
        : '/dashboard/stats/progress';
      const here =
        (this.props.history?.location?.pathname || '') +
        (this.props.history?.location?.search || '');
      if (this.props.history && here !== dest) {
        this.props.history.push(dest);
      }
    }
  }

  //add lean body mass to manualMeasurement
  addLeanBodyMassAndWeight = () => {
    let bodyFatObj = { ...this.state.bodyFatObj };
    // `weightInputUnit` is the unit the trainer was actively typing
    // in — independent of the client's `data.isMetric` storage
    // preference. Conversion to the canonical kg/lb pair derives
    // from THIS, not from the client's profile flag. Before this
    // change, the conversion read `data.isMetric` which silently
    // mis-stored values when the trainer's mental unit didn't
    // match the client's profile (the Sara bug source).
    const inputIsMetric = this.state.weightInputUnit !== 'lb';

    if (bodyFatObj.bfPercentage === '' || bodyFatObj.bfPercentage === 'e') {
      // No body fat entered → can't derive lean mass. Save bf% as 0
      // (Withings-compatibility convention) and leave leanBodyMass
      // unset. The previous behavior set LBM = total body weight,
      // which surfaced in the V2 measurements tab as a (wrong)
      // "Lean mass: 150 kg" tile when the client's actual weight
      // was 150 kg. Display surfaces already render "—" for missing
      // leanBodyMass — see the falsy-check in AdminClientsProfilePageV2.
      bodyFatObj.bfPercentage = 0;
      delete bodyFatObj.leanBodyMass;
    } else {
      let leanBodyMass = (
        bodyFatObj.weight -
        (bodyFatObj.bfPercentage / 100) * bodyFatObj.weight
      ).toFixed(2);
      bodyFatObj.leanBodyMass = inputIsMetric
        ? { kg: leanBodyMass, lb: kgsToPounds(leanBodyMass) }
        : { kg: poundsToKgs(leanBodyMass), lb: leanBodyMass };
    }
    bodyFatObj.weight = {
      kg: inputIsMetric
        ? bodyFatObj.weight
        : poundsToKgs(bodyFatObj.weight),
      lb: !inputIsMetric
        ? bodyFatObj.weight
        : kgsToPounds(bodyFatObj.weight),
    };

    this.setState({ bodyFatObj: bodyFatObj });

    this.props.saveBodyFat({
      bodyFatObj,
      progressPictures: this.state.progressPictures,
    });
    // this.props.getTodo(); // get todo after submission
    this.setState({ progressPictures: [] });
  };

  handleInputChange = (value, key) => {
    let bodyFatObj = { ...this.state.bodyFatObj };
    bodyFatObj.userId = this.props.data.user._id;
    bodyFatObj[key] = value;
    bodyFatObj.bfPercentage = '';
    bodyFatObj.source = hasMinimumRole(this.props.user.role, 'trainer')
      ? 'Coach'
      : 'Manual Measurement';
    bodyFatObj.type = 'Manual';
    bodyFatObj.pictures = [];
    bodyFatObj.measurements = {};
    bodyFatObj[key] = value;
    this.setState({ bodyFatObj: bodyFatObj });
  };

  handleImageInputChange = (value) => {
    this.setState({
      progressPictures: value,
    });
  };

  // Native date input emits "YYYY-MM-DD"; coerce so downstream consumers
  // (the redux saga + the Mongo doc) keep getting a Date instance.
  onDateChange = (e) => {
    const value = e.target.value;
    let bodyFatObj = { ...this.state.bodyFatObj };
    bodyFatObj.date = value ? new Date(value) : null;
    this.setState({ bodyFatObj });
  };

  render() {
    const { bodyFatObj, progressPictures } = this.state;
    const { user, saveBodyFatError, saveBodyFatLoading } = this.props;
    const errorMsgs =
      saveBodyFatError &&
      saveBodyFatError.type === 'Manual' &&
      Array.isArray(saveBodyFatError.errors)
        ? saveBodyFatError.errors.map((err) => err.msg).filter(Boolean)
        : [];

    return (
      <div className="mf2">
        <div className="mf2-grid">
          <div className="mf2-field">
            <WeightField
              id="mf2-weight"
              label="Weight"
              required
              value={bodyFatObj.weight ?? ''}
              unit={this.state.weightInputUnit}
              onChange={(value, unit) => {
                this.handleInputChange(value, 'weight');
                if (unit !== this.state.weightInputUnit) {
                  this.setState({ weightInputUnit: unit });
                }
              }}
            />
          </div>
          <div className="mf2-field">
            <label className="mf2-label" htmlFor="mf2-bf">
              Body fat
            </label>
            <div className="mf2-input-wrap">
              <input
                id="mf2-bf"
                type="number"
                inputMode="decimal"
                className="mf2-input"
                value={bodyFatObj.bfPercentage ?? ''}
                onChange={(e) =>
                  this.handleInputChange(e.target.value, 'bfPercentage')
                }
                placeholder="Optional"
              />
              <span className="mf2-input-suffix">%</span>
            </div>
          </div>
        </div>

        <div className="mf2-uploader">
          <ProgressUpload
            handler={this.handleImageInputChange}
            location="manual"
            photos={progressPictures}
            clientId={this.props.data?.user?._id}
          />
        </div>

        {user && hasMinimumRole(user.role, 'trainer') ? (
          <div className="mf2-field">
            <label className="mf2-label" htmlFor="mf2-date">
              Date taken
            </label>
            <input
              id="mf2-date"
              type="date"
              className="mf2-input"
              defaultValue={moment().format('YYYY-MM-DD')}
              onChange={this.onDateChange}
            />
          </div>
        ) : null}

        {errorMsgs.length > 0 ? (
          <div className="mf2-error" role="alert">
            {errorMsgs.map((m, i) => (
              <p key={i}>{m}</p>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className="btn btn-grad mf2-submit"
          onClick={() => this.addLeanBodyMassAndWeight()}
          disabled={!bodyFatObj.weight || saveBodyFatLoading}
        >
          {saveBodyFatLoading ? 'Saving…' : 'Save measurement'}
        </button>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    saveBodyFat: (obj) => dispatch(saveBodyFat(obj)),
    clearBodyFatErrors: () => dispatch(clearBodyFatErrors()),
    getNutrition: () => dispatch(getNutrition()),
    closeDrawer: () => {
      return dispatch({
        type: CLOSE_DRAWER,
      });
    },
    getTodo: () => dispatch(getTodo()),
  };
};

const mapStateToProps = (state) => {
  return {
    bodyFatError: state.measurements.bodyFatError,
    saveBodyFatLoading: state.measurements.saveBodyFatLoading,
    userBodyFatMeasurements: state.measurements.userBodyFatMeasurements,
    saveBodyFatError: state.measurements.saveBodyFatError,
    // Trainer's preferred input unit (Phase 2). Used by the
    // constructor below to default the WeightField pill — a US
    // trainer working with metric clients lands on `lb`
    // automatically. Clients see `undefined` here and fall back
    // to `data.isMetric`.
    viewerPreferredUnits:
      state.auth?.user?.trainerPreferences?.preferredUnits || null,
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ManualForm)
);
